import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickMarketPriceBasis } from "@/features/pathpick/types";
import {
  PathPickMarketMetrics,
  PATH_PICK_MARKET_BASIS_LABEL_KEY,
  PATH_PICK_MARKET_LABEL,
  PATH_PICK_MARKET_SALES_LABEL_KEY,
} from "@/features/pathpick/utils/market";

interface Props {
  metrics: PathPickMarketMetrics;
  basis: PathPickMarketPriceBasis;
  onOpenMarginFormula: () => void;
}

interface CellProps {
  label: string;
  children: React.ReactNode;
  // 첫 칸을 뺀 나머지에 세로 구분선을 둔다.
  hasDivider: boolean;
}

const Cell = ({ label, children, hasDivider }: CellProps) => (
  <Flex
    flexDirection={"column"}
    gap={"2px"}
    flex={1}
    minWidth={0}
    paddingLeft={hasDivider ? "12px" : undefined}
    borderLeft={hasDivider ? "0.5px solid #EFEFED" : undefined}
  >
    <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
      {label}
    </Text>
    {children}
  </Flex>
);

/**
 * 상세 모달의 마켓 1개 요약 카드 — `KREAM · 최저 입찰가` 헤더 + 기준가 / 거래수 / 예상 마진.
 *
 * 미매칭 마켓도 카드 자리를 비우지 않고 그대로 그린다. 두 카드가 나란히 있어야
 * "한쪽만 붙어 있다"는 사실이 보이고, 기준을 토글해도 레이아웃이 흔들리지 않는다.
 */
function PathPickProductDetailMarketCard({
  metrics,
  basis,
  onOpenMarginFormula,
}: Props) {
  const { t, tNumberString, tCurrencyString } = useI18nHelper();
  const marketLabel = PATH_PICK_MARKET_LABEL[metrics.market];
  const basisLabel = t(
    PATH_PICK_MARKET_BASIS_LABEL_KEY[metrics.market][basis]
  );

  const formatKrw = (value?: number | null) =>
    value != null
      ? tCurrencyString(value, { type: "SYMBOL", code: "KRW" })
      : "-";

  return (
    <Flex
      flexDirection={"column"}
      gap={"8px"}
      padding={"12px"}
      border={"0.5px solid #D4D4D1"}
      borderRadius={"8px"}
    >
      <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
        {`${marketLabel} · ${basisLabel}`}
      </Text>
      <Flex gap={"12px"}>
        <Cell
          label={t("path_pick:search_products.size_stock_modal.basis_price")}
          hasDivider={false}
        >
          {metrics.isMatched ? (
            <Text textStyle={"body-sm-bold"} color={"#444440"}>
              {formatKrw(metrics.basisValue)}
            </Text>
          ) : (
            <Text textStyle={"body-sm-bold"} color={"#B27700"}>
              {t("path_pick:market.unmatched", { market: marketLabel })}
            </Text>
          )}
        </Cell>
        <Cell
          label={t(PATH_PICK_MARKET_SALES_LABEL_KEY[metrics.market])}
          hasDivider
        >
          <Text textStyle={"body-sm-bold"} color={"#444440"}>
            {metrics.salesCount != null
              ? t("path_pick:search_products.popular.bid_count_value", {
                  value: tNumberString(metrics.salesCount),
                })
              : "-"}
          </Text>
        </Cell>
        <Cell
          label={t("path_pick:search_products.product_list.expected_margin")}
          hasDivider
        >
          {metrics.expectedMargin != null ? (
            <Box
              cursor={"pointer"}
              onClick={onOpenMarginFormula}
              color={metrics.expectedMargin < 0 ? "#CC1D1D" : "#16B41C"}
            >
              <Text textStyle={"body-sm-bold"} textDecoration={"underline"}>
                {formatKrw(metrics.expectedMargin)}
              </Text>
              {metrics.marginRate != null && (
                <Text
                  textStyle={"caption-medium"}
                  textDecoration={"underline"}
                >
                  {`${metrics.marginRate.toFixed(1)}%`}
                </Text>
              )}
            </Box>
          ) : (
            <Text textStyle={"body-sm-bold"} color={"#8F8F8C"}>
              {t(
                "path_pick:search_products.product_list.card.margin_calc_unavailable"
              )}
            </Text>
          )}
        </Cell>
      </Flex>
    </Flex>
  );
}

export default PathPickProductDetailMarketCard;
